<template>
	<Card :title="enrollment.user.last_name" :subtitle="enrollment.user.names">
		<template #badges>
			<Badge :color="badgeColor" active>{{ trans(`enrollments.status.${enrollment.status}.title`) }}</Badge>
			<Badge class="cursor-pointer" @click.stop="copyTextToClipboard(enrollment.id.toString())">#{{ enrollment.id }}</Badge>
		</template>
		<div v-if="enrollment.user.citizenships.length > 0" class="flex items-center flex-wrap gap-2">
			<div v-for="citizenship in enrollment.user.citizenships" class="flex flex-col items-center gap-1">
				<CountryFlag :iso="citizenship.code" mode="rounded"/>
				<p class="text-[8px]">{{ citizenship.name }}</p>
			</div>
		</div>
		<LabeledObject>
			<template #label>{{ trans('account.id_number') }}</template>
			<p class="hover:underline cursor-pointer place-self-start" @click.stop="copyTextToClipboard(enrollment.user.id_number)">{{ enrollment.user.id_number }}</p>
		</LabeledObject>
		<LabeledObject>
			<template #label>{{ trans('enrollments.created_at') }}</template>
			<p>{{ dayjs(enrollment.created_at).format(trans('general.dayjs.datetime')) }}</p>
		</LabeledObject>
		<LabeledObject v-if="enrollment.user.colleges.length > 0">
			<template #label>{{ trans('account.college_programs.title') }}</template>
			<p v-for="college in enrollment.user.colleges">{{ college.name }}</p>
		</LabeledObject>
		<LabeledObject>
			<template #label>{{ trans('enrollments.price') }}</template>
			<p>$ {{ formatNumber(enrollment.total_price, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</p>
		</LabeledObject>
		<template #footer>
			<a v-if="canUpdate" :href="route('events.enrollments.show', [enrollment.event_id, enrollment.id])" target="_blank">
				<SecondaryButton :color="Color.Blue">
					<i class="fa-solid fa-edit mr-2"/>
					{{ trans('general.edit') }}
				</SecondaryButton>
			</a>
			<SoftButton v-if="canDelete" :color="Color.Red" @click.stop="destroy">
				<i class="fa-solid fa-trash mr-2"/>
				{{ trans('general.delete') }}
			</SoftButton>
		</template>
	</Card>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import {type Enrollment} from '@/Types/Events/Enrollment'
import CountryFlag from '@dzangolab/vue-country-flag-icon'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import {computed, ComputedRef, defineProps, type Ref, ref} from 'vue'
import {getEnrollmentStatusColor} from '@/Helpers/Events/enrollments'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {copyTextToClipboard, formatNumber, showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	canMark:boolean,
	canUpdate:boolean,
	canDelete:boolean,
	enrollment:Enrollment,
}>()

const emit = defineEmits([
	'deleted',
])

const working:Ref<boolean> = ref(false)
const enrollment:Ref<Enrollment> = ref(props.enrollment)

const badgeColor:ComputedRef<Color> = computed(():Color => {
	return getEnrollmentStatusColor(enrollment.value.status)
})

function destroy():void{
	if(!working.value && confirm(trans('enrollments.delete.confirm'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.enrollments.delete', {event: enrollment.value.event_id, enrollment: enrollment.value.id})).then(() => {
			emit('deleted', enrollment.value.id)
			showSuccess(trans('general.deleted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>